/**
 * Reach state, save and command contracts (M3 plan §2.6.2). FROZEN after R1: later tasks may add fields or
 * exports, never rename or remove them. R5 owns the state functions and the save; the runtime (R12) and the
 * subsystems (R17-R19) handle the commands.
 */
import type { FactionKey, QuatT, Vec3T } from '../galaxy/layout/types'

export type { FactionKey, QuatT, Vec3T } from '../galaxy/layout/types'

export type PartId = 'hull_plating' | 'electronics' | 'drive_parts' // M4 component wares (sim spec §3.1)
export const PART_IDS: readonly PartId[] = ['hull_plating', 'electronics', 'drive_parts']
export type Knowledge = 'unknown' | 'rumoured' | 'charted'
export type RelayStatus = 'online' | 'offline' | 'reboot'
export interface RelayState {
  status: RelayStatus; delivered: Partial<Record<PartId, number>>; attempts: number; scanned: boolean
  sponsor: FactionKey | null; sponsorPending: boolean; relinkedAt: number | null // game seconds
}
export interface SystemDelta {
  rocks: Record<string, number> // rock key → remaining fraction (0 = gone); M2 mining writes it after the bridge
  lootedPois: string[]; clearedBases: string[]
  wrecks: unknown[] // M2-owned shape, kept opaque by M3
}
export interface ChartLedger {
  unsold: Record<string, number>; soldTotal: Record<string, number>; firstDiscoveries: string[]
  surveyed?: string[] // M8 MC-a10: systems charted by a bought Holdfast survey (never pay system or anchor chart value)
}
export interface StandInState { credits: number; cargo: Record<PartId, number>; capacityT: number; scannerMk: 1 | 2 | 3 }
export interface ReachLocation { systemId: string; position: Vec3T; rotation: QuatT; stationId: string | null }
export type NavTargetKind = 'station' | 'relay' | 'poi' | 'body' | 'field' | 'laneRing' | 'hulk' // hulk: M9 CH-14a (reach/navPoints.ts)
export interface NavTargetRef { kind: NavTargetKind; id: string }
export interface ReachState {
  galaxySeed: number; gameTime: number
  home: { systemId: string; stationId: string }
  lastSafe: ReachLocation // where a load resumes (§2.9 flow 12)
  knowledge: Record<string, 'rumoured' | 'charted'> // absent = unknown
  discoveredPois: Record<string, string[]>
  relays: Record<string, RelayState> // absent = the generator's online flag, with no deliveries
  laneRings: Record<string, boolean> // absent = LaneRingSite.initiallyUp
  deltas: Record<string, SystemDelta>
  charts: ChartLedger
  route: string[] | null; navTarget: NavTargetRef | null
  standIn: StandInState | null // null once the bridge hands the career to M2
}
export interface ReachSave { kind: 'eclipse.reach'; version: 1; savedAt: string; state: ReachState }

export type ReachCommand =
  | { kind: 'setNavTarget'; target: NavTargetRef | null } // travel (R17)
  | { kind: 'plotRoute'; toSystemId: string; avoidHostile: boolean } // travel
  | { kind: 'clearRoute' } // travel
  | { kind: 'jettison' } // travel (M8 TR-8b1): ends an interdiction tug; refused unless interdicted (M6 spec §6.4.5)
  | { kind: 'sellCharts'; stationId: string } // exploration (R18)
  | { kind: 'buyScanner'; stationId: string; mk: 2 | 3 } // exploration
  | { kind: 'buySurvey'; stationId: string } // exploration (M8 MC-a10)
  | { kind: 'buyParts'; stationId: string; part: PartId; qty: number } // relink (R19)
  | { kind: 'deliverParts'; relayId: string } // relink
  | { kind: 'beginReboot'; relayId: string } // relink
  | { kind: 'repairLaneRing'; ringId: string } // relink
  | { kind: 'chooseSponsor'; relayId: string; faction: FactionKey } // relink
  | { kind: 'setHome'; stationId: string } // runtime (R12)
  | { kind: 'saveNow' } // runtime
  | { kind: 'newGalaxy'; seed: number } // runtime (reloads with ?galaxy=)
export type ReachCommandError = 'unavailable' | 'outOfRange' | 'funds' | 'cargo' | 'invalid' | 'busy'
export type ReachCommandResult = { ok: true; message: string } | { ok: false; error: ReachCommandError; message: string }
